import { motion } from "framer-motion";
import { Phone, MapPin, ShieldCheck, Star, Clock, ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";
import { SITE, IMAGES } from "../../data/site";

export const CityHero = ({ city }) => {
  return (
    <section
      id="top"
      data-testid={`city-hero-${city.slug}`}
      className="relative pt-28 md:pt-32 pb-16 md:pb-24 bg-[#F9F7F4] overflow-hidden grain"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 relative">
        {/* Breadcrumb */}
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-2 text-xs md:text-sm text-[#5C5A55] mb-10"
        >
          <Link to="/" className="hover:text-[#C05746] link-underline">
            Home
          </Link>
          <ChevronRight size={12} />
          <Link to="/#areas" className="hover:text-[#C05746] link-underline">
            Service Areas
          </Link>
          <ChevronRight size={12} />
          <span className="text-[#1C1B1A] font-semibold">
            {city.city}, {city.stateCode}
          </span>
        </nav>

        <div className="grid grid-cols-12 gap-8 md:gap-12 items-end">
          <div className="col-span-12 lg:col-span-7 relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="flex items-center gap-3 mb-6"
            >
              <span className="h-px w-10 bg-[#C05746]" />
              <span className="text-[#C05746] text-xs md:text-sm font-semibold tracking-[0.24em] uppercase flex items-center gap-2">
                <MapPin size={14} /> {city.city}, {city.state}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.05 }}
              className="font-display text-[#1C1B1A] text-[2.75rem] sm:text-[3.75rem] md:text-[4.5rem] lg:text-[5.5rem] leading-[0.98] tracking-[-0.03em]"
              data-testid="city-headline"
            >
              Termite Control in
              <br />
              <span className="font-serif-accent italic text-[#C05746]">
                {city.city}
              </span>
              , {city.stateCode}.
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
              className="mt-6 max-w-xl text-[#1C1B1A] text-lg md:text-xl leading-relaxed font-medium"
            >
              {city.hook}
            </motion.p>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="mt-4 max-w-xl text-[#5C5A55] text-base md:text-lg leading-relaxed"
            >
              {city.intro}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4"
            >
              <a
                href={SITE.phoneHref}
                data-testid={`city-call-button-${city.slug}`}
                className="cta-shimmer group inline-flex items-center gap-4 bg-[#1E2C25] hover:bg-[#2A3B32] text-[#F9F7F4] pl-7 pr-8 py-5 md:py-6 transition-all hover:-translate-y-0.5"
              >
                <span className="flex items-center justify-center w-10 h-10 md:w-12 md:h-12 bg-[#C05746] text-[#F9F7F4] -ml-3 -my-3 md:-my-4">
                  <Phone size={20} strokeWidth={2.5} />
                </span>
                <span className="flex flex-col items-start leading-tight">
                  <span className="text-[10px] md:text-[11px] uppercase tracking-[0.24em] text-[#F9F7F4]/60">
                    Call {city.city}'s termite experts
                  </span>
                  <span className="font-display text-2xl md:text-3xl tracking-tight">
                    {SITE.phoneDisplay}
                  </span>
                </span>
              </a>
              <div className="text-xs md:text-sm text-[#5C5A55]">
                <div className="font-semibold text-[#1C1B1A]">Fast scheduling in {city.city}</div>
                <div>{SITE.hours}</div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.45 }}
              className="mt-10 md:mt-14 grid grid-cols-3 max-w-lg border-t border-[#E5DFD5]"
            >
              {[
                { icon: ShieldCheck, label: "A+ BBB", sub: "Accredited" },
                { icon: Star, label: "4.8★", sub: "148+ reviews" },
                { icon: Clock, label: "Fast", sub: "Scheduling" },
              ].map((t) => (
                <div key={t.label} className="pt-5 pr-4">
                  <t.icon size={18} className="text-[#C05746] mb-2" />
                  <div className="font-display text-[#1C1B1A] text-base leading-tight">{t.label}</div>
                  <div className="text-xs text-[#5C5A55]">{t.sub}</div>
                </div>
              ))}
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.98, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="col-span-12 lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] bg-[#1E2C25] overflow-hidden">
              <img
                src={IMAGES.hero}
                alt={`Suburban home in ${city.city}, ${city.stateCode} protected by ATC Pest Control`}
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1E2C25]/50 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between gap-4 text-[#F9F7F4]">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#F9F7F4]/70">
                    Serving {city.stateCode}
                  </div>
                  <div className="font-display text-2xl md:text-3xl mt-1 leading-tight">
                    {city.city}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase tracking-[0.24em] text-[#F9F7F4]/70">ZIP</div>
                  <div className="font-display text-2xl md:text-3xl mt-1 leading-tight">
                    {city.zip}
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden lg:block absolute -left-6 -bottom-6 w-28 h-28 bg-[#C05746] z-[-1]" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};
